import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ShowChart as ShowChartIcon,
  BarChart as BarChartIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import {
  SymbolInfo,
  FundamentalData,
  CompanyProfile,
} from "react-tradingview-embed";
import { throttle } from "lodash";
import axios from "axios";
import CommunityLeftSidebar from "../components/CommunityLeftSidebar";
import ResponsiveTopBar from "../components/ResponsiveTopBar";
import MobileNavDrawer from "../components/MobileNavDrawer";
import MobileWidgetDrawer from "../components/MobileWidgetDrawer";
import AdvancedChart2 from "../components/AdvancedChart2";
import PostCommunity from "../components/PostCommunity";
import { useTheme } from "../components/ThemeContext";
import { DeviceContext } from "../context/DeviceContext";

const TOPBAR_HEIGHT = 64;
const SIDEBAR_LEFT_WIDTH = 264;
const SIDEBAR_RIGHT_WIDTH = 400;

// ── Right sidebar — stock info widgets ───────────────────────────────────────
const RightStocksBar = React.memo(({ ticker }) => (
  <Box sx={{ p: 2 }}>
    <SymbolInfo
      widgetProps={{ symbol: ticker, width: "100%", height: 400, locale: "en" }}
    />
    <Box sx={{ mt: 2 }}>
      <FundamentalData
        widgetProps={{
          symbol: ticker,
          width: "100%",
          height: 500,
          colorTheme: "dark",
          isTransparent: false,
          displayMode: "regular",
          locale: "en",
        }}
      />
    </Box>
    <Box sx={{ mt: 2 }}>
      <CompanyProfile
        widgetProps={{
          symbol: ticker,
          width: "100%",
          height: 500,
          colorTheme: "dark",
          isTransparent: false,
          locale: "en",
        }}
      />
    </Box>
  </Box>
));

// ── Page ─────────────────────────────────────────────────────────────────────
function StocksPage() {
  const { stockTicker } = useParams();
  const { theme } = useTheme();
  const { isMobile } = useContext(DeviceContext);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);

  // URL uses "-" as namespace separator, TradingView uses ":"
  const ticker = stockTicker.replace("-", ":");

  const updatePosts = (postId, upvotes, downvotes) =>
    setPosts((prev) =>
      prev.map((p) => (p.postId === postId ? { ...p, upvotes, downvotes } : p))
    );

  const fetchPosts = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/posts/byCommunityOrTags`,
        { params: { communityName: stockTicker, tags: stockTicker, sortBy: "hot", page, size: 20 } }
      );
      const data = res.data;
      setPosts((prev) => {
        const fresh = data.content.filter((p) => !prev.some((ep) => ep.postId === p.postId));
        return [...prev, ...fresh];
      });
      setHasMore(!data.last);
    } catch (e) {
      console.error("Error fetching posts:", e);
    } finally {
      loadingRef.current = false;
    }
  }, [page, stockTicker, hasMore]);

  useEffect(() => { fetchPosts(); }, [page, fetchPosts]);

  useEffect(() => {
    const onScroll = throttle(() => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight * 0.75 &&
        hasMore && !loadingRef.current
      ) setPage((p) => p + 1);
    }, 300);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasMore]);

  // ── Mobile widget list — chart first, then TradingView info widgets ────────
  const mobileWidgets = [
    {
      id: "chart",
      label: "Chart",
      icon: ShowChartIcon,
      activeColor: "#05df72",
      activeBg: "rgba(0,201,80,0.12)",
      activeBorder: "rgba(0,201,80,0.35)",
      render: () => (
        <Box sx={{ p: 1 }}>
          <AdvancedChart2 stockTicker={stockTicker} isMobile={true} />
        </Box>
      ),
    },
    {
      id: "symbolinfo",
      label: ticker.split(":")[1] || ticker,
      icon: ShowChartIcon,
      activeColor: "#05df72",
      activeBg: "rgba(0,201,80,0.12)",
      activeBorder: "rgba(0,201,80,0.35)",
      render: () => (
        <Box sx={{ p: 1 }}>
          <SymbolInfo widgetProps={{ symbol: ticker, width: "100%", height: 400, locale: "en" }} />
        </Box>
      ),
    },
    {
      id: "fundamentals",
      label: "Fundamentals",
      icon: BarChartIcon,
      activeColor: "#38bdf8",
      activeBg: "rgba(56,189,248,0.12)",
      activeBorder: "rgba(56,189,248,0.35)",
      render: () => (
        <Box sx={{ p: 1 }}>
          <FundamentalData
            widgetProps={{ symbol: ticker, width: "100%", height: 500, colorTheme: "dark", isTransparent: false, displayMode: "regular", locale: "en" }}
          />
        </Box>
      ),
    },
    {
      id: "company",
      label: "Company",
      icon: BusinessIcon,
      activeColor: "#a78bfa",
      activeBg: "rgba(167,139,250,0.12)",
      activeBorder: "rgba(167,139,250,0.35)",
      render: () => (
        <Box sx={{ p: 1 }}>
          <CompanyProfile
            widgetProps={{ symbol: ticker, width: "100%", height: 500, colorTheme: "dark", isTransparent: false, locale: "en" }}
          />
        </Box>
      ),
    },
  ];

  const scrollbarStyles = {
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      borderRadius: 2,
    },
  };

  const postsFeed = (
    <Box>
      {posts.map((post) => (
        <PostCommunity key={post.postId} post={post} updatePosts={updatePosts} />
      ))}
    </Box>
  );

  const desktopCenterContent = (
    <Box>
      <AdvancedChart2 stockTicker={stockTicker} isMobile={false} />
      <Box sx={{ mt: 2 }}>{postsFeed}</Box>
    </Box>
  );

  return (
    <>
      <ResponsiveTopBar
        isMobile={isMobile}
        onMobileMenuToggle={() => setMobileMenuOpen((p) => !p)}
      />
      <MobileNavDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {isMobile ? (
        // ── MOBILE: flat layout, widget drawer for stock info ───────────────
        <Box
          sx={{
            mt: `${TOPBAR_HEIGHT}px`,
            minHeight: "100vh",
            backgroundColor: theme.palette.background.default,
            px: 2,
            py: 2,
            pb: "6rem",
          }}
        >
          {postsFeed}
          <MobileWidgetDrawer widgets={mobileWidgets} />
        </Box>
      ) : (
        // ── DESKTOP: three-column fixed layout ─────────────────────────────
        <Box
          sx={{
            display: "flex",
            minHeight: "100vh",
            backgroundColor: theme.palette.background.default,
            pt: `${TOPBAR_HEIGHT}px`,
          }}
        >
          {/* Left sidebar */}
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              position: "fixed",
              top: TOPBAR_HEIGHT,
              left: 0,
              bottom: 0,
              width: SIDEBAR_LEFT_WIDTH,
              backgroundColor: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
              overflowY: "auto",
              overflowX: "hidden",
              ...scrollbarStyles,
              zIndex: 100,
            }}
          >
            <Box sx={{ pt: 2, px: 2, pb: 4 }}>
              <CommunityLeftSidebar />
            </Box>
          </Box>

          {/* Center — chart + posts */}
          <Box
            sx={{
              ml: { xs: 0, lg: `${SIDEBAR_LEFT_WIDTH}px` },
              mr: { xs: 0, lg: `${SIDEBAR_RIGHT_WIDTH}px` },
              flex: 1,
              minWidth: 0,
              py: 4,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box sx={{ width: "100%", maxWidth: 800, px: { md: 3, lg: 4 } }}>
              {desktopCenterContent}
            </Box>
          </Box>

          {/* Right sidebar — stock info */}
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              position: "fixed",
              top: TOPBAR_HEIGHT,
              right: 0,
              bottom: 0,
              width: SIDEBAR_RIGHT_WIDTH,
              backgroundColor: theme.palette.background.paper,
              borderLeft: `1px solid ${theme.palette.divider}`,
              overflowY: "auto",
              overflowX: "hidden",
              ...scrollbarStyles,
              zIndex: 100,
            }}
          >
            <RightStocksBar ticker={ticker} />
          </Box>
        </Box>
      )}
    </>
  );
}

export default StocksPage;
